import { createFileRoute } from '@tanstack/react-router'
import ProjectListPage from '../../Pages/ProjectListPage/ProjectListPage'
import ProjectListPageHeader from '../../Pages/ProjectListPage/ProjectListPageHeader'
import PageBody from '../../Pages/Layout/PageBody'
import SessionStore from '../../State/SessionStore'
import {
    canViewRoute,
    migratedToV2,
} from '../../State/Permissions/HasPermissions'
import PermissionDenied from '../../Pages/PermissionDenied'
import LoadingSpinner from '../../Components/LoadingSpinner'
import ErrorComponent from '../../Components/ErrorComponent'
import { trpc } from '../../system/trpc'
import { ProjectReportPage } from '@2/pages/project-report/project-report-page'
import {
    setOrganisationReport,
    setReportData,
} from '@2/pages/project-report/project-report-store'
import { ProjectReportColumns } from '../../reports/Projects/ProjectReportColumns'
import { dateStringLookup } from '../../Components/Filters'
import { format } from 'date-fns'
import { setLayoutDefaults } from '@2/layout/layout-store'
import { getCacheItemById } from '@2/cache'
import { queryClient } from '../../App'
import { PageBody as V2PageBody } from '@2/layout/page-body'
import { processTRPCData } from '../../Queries/fetchData'

export const Route = createFileRoute('/_private/projects/')({
    beforeLoad: async ({ params, search }) => {
        if (!canViewRoute(SessionStore.user, 'projectList', params))
            throw new Error('Permission Denied')
        const report = getCacheItemById('organisationReports', search.report)

        setLayoutDefaults({
            pageTitle: report?.name || 'Project Report',
            subMenu: 'projects',
        })
    },
    errorComponent: ({ error, reset }) => {
        if (error.message === 'Permission Denied') return <PermissionDenied />
        return <ErrorComponent error={error} reset={reset} />
    },
    loader: async ({ location }) => await loader(location.search),
    loaderDeps: ({ search: { report } }) => ({
        report,
    }),
    pendingComponent: LoadingSpinner,
    component: Wrapper,
})

const loader = async ({ report }) => {
    const reportId = report || SessionStore.organisation?.defaultProjectReportId
    if (migratedToV2()) {
        const res = reportId
            ? await trpc.projectReport.getOrganisationReport.query({
                  reportId,
              })
            : null

        const organisationReport =
            res || SessionStore.organisation.defaultProjectReport
        let dateRange = [null, null]
        if (organisationReport.options?.dateRange) {
            dateRange =
                dateStringLookup[organisationReport.options?.dateRange]?.(
                    organisationReport.options?.fortnightType
                ) || organisationReport.options?.dateRange
        }
        const { columns, filters, groupBy, sortBy, options, name } =
            organisationReport
        setOrganisationReport(
            {
                columns,
                filters,
                groupBy,
                organisationId: SessionStore.organisationId,
                sortBy,
                dateRange,
                invoiceDateType: SessionStore.settings.reportInvoiceDateType,
                id: reportId,
                options,
                name,
            },
            { needsRefetch: false }
        )
        const cacheData = await queryClient.fetchQuery({
            queryKey: ['projectReportCache', reportId],
            queryFn: () =>
                trpc.projectReport.getReportsData.query({
                    columns: organisationReport?.columns.filter(
                        (c) =>
                            !ProjectReportColumns(reportId)[c]?.permissions ||
                            ProjectReportColumns(reportId)[c].permissions()
                    ),
                    filters: organisationReport?.filters.map((f) => {
                        if (
                            ProjectReportColumns(reportId)[f.column]?.type ===
                            'date'
                        ) {
                            let value = dateStringLookup[f.value]?.() || f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) =>
                                    d ? format(d, 'yyyy-MM-dd') : null
                                )
                            } else {
                                value = value
                                    ? format(value, 'yyyy-MM-dd')
                                    : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else if (
                            ProjectReportColumns(reportId)[f.column]?.type ===
                            'time'
                        ) {
                            let value = f.value
                            if (Array.isArray(value)) {
                                value = value.map((d) => {
                                    d = new Date(d)
                                    return d && d.getTime()
                                        ? format(d, 'yyyy-MM-dd HH:mm')
                                        : null
                                })
                            } else {
                                value = new Date(value)
                                value =
                                    value && value.getTime()
                                        ? format(value, 'yyyy-MM-dd HH:mm')
                                        : null
                            }
                            return {
                                ...f,
                                value,
                            }
                        } else {
                            return f
                        }
                    }),
                    groupBy: ['projectId'],
                    sortBy: organisationReport?.sortBy || [],
                    dateRange,
                    invoiceDateType:
                        SessionStore.settings.reportInvoiceDateType,
                }),
            staleTime: 5 * 60 * 1000,
        })

        setReportData(cacheData)
    }

    const data = await trpc.legacy.getProjectListPageData.query({ reportId })
    return processTRPCData(data)
}

function Wrapper() {
    if (migratedToV2()) {
        return (
            <>
                <V2PageBody>
                    <ProjectReportPage />
                </V2PageBody>
            </>
        )
    }
    return (
        <>
            <ProjectListPageHeader
                {...Route.useParams()}
                {...Route.useSearch()}
            />
            <PageBody>
                <ProjectListPage
                    {...Route.useParams()}
                    {...Route.useSearch()}
                />
            </PageBody>
        </>
    )
}
